import type { MachineState } from '@tabel/shared/schemas/cells';
import type { Status } from '@tabel/shared/schemas/timesheets';

export const STATUS_LABEL: Record<Status, string> = {
  draft: 'Черновик',
  completed: 'Завершён',
  returned: 'Возвращён',
  signed: 'Подписан',
};

export const STATE_LABEL: Record<MachineState, string> = {
  ok: '',
  empty: '',
  no_exit: 'нет выхода',
  no_entry: 'нет входа',
  on_site: 'на объекте',
  error: 'ошибка расчёта',
  over_24h: 'больше 24 ч — проверьте',
};

export const STATE_SHORT: Record<MachineState, string> = {
  ok: '',
  empty: '',
  no_exit: 'н/вых',
  no_entry: 'н/вх',
  on_site: 'на об.',
  error: 'ошибка',
  over_24h: '>24 ч',
};

export function ruDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

/** Момент UTC → «дд.мм.гггг чч:мм» по смещению объекта (например, +05:00). */
export function ruDateTime(iso: string | null, tzOffset = '+05:00'): string {
  if (!iso) return '—';
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(tzOffset);
  const off = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) * 60_000 : 0;
  const local = new Date(Date.parse(iso) + off).toISOString();
  return `${ruDate(local)} ${local.slice(11, 16)}`;
}

/** Момент UTC → «дд.мм, чч:мм» по смещению объекта: «Данные СКУД: 14.10, 10:42» (003 FR-039). */
export function ruShortDateTime(iso: string, tzOffset = '+05:00'): string {
  const full = ruDateTime(iso, tzOffset); // «дд.мм.гггг чч:мм»
  return `${full.slice(0, 5)}, ${full.slice(11, 16)}`;
}

/** Сегодня по времени объекта (`YYYY-MM-DD`); `now` — для тестов. */
export function localToday(tzOffset = '+05:00', now = Date.now()): string {
  const local = ruDateTime(new Date(now).toISOString(), tzOffset); // «дд.мм.гггг чч:мм»
  return `${local.slice(6, 10)}-${local.slice(3, 5)}-${local.slice(0, 2)}`;
}

export function hours(v: number | null): string {
  return v == null ? '' : v.toFixed(1).replace('.', ',');
}

/**
 * Часы для показа: табель на движке присылает значение в формате R5 правил объекта (`value`, MVP C3) — показываем его
 * как есть, интерфейс не округляет; табель 001 — только число, десятые с запятой.
 */
export function shownHours(value: string | null | undefined, v: number | null): string {
  return value ?? hours(v);
}

/** Нерабочий день табеля для подсказки (Q-13): «праздник», «выходной»; рабочий — `null`. */
export function dayOffLabel(d: { weekend: boolean; holiday?: boolean } | undefined): string | null {
  if (d?.holiday) return 'праздник';
  return d?.weekend ? 'выходной' : null;
}

/** Другие бригады дня (Q-15) для текста: «Бригада-2», «Бригада-3». */
export function brigadeList(names: readonly string[]): string {
  return names.map((n) => `«${n}»`).join(', ');
}

/** Предупреждение движка за день для строки текста: с заглавной буквы («Потерян проход — проверить»). */
export function engineNote(note: string): string {
  return note.charAt(0).toUpperCase() + note.slice(1);
}

const WD = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
export function weekday(iso: string): string {
  return WD[new Date(`${iso}T00:00:00Z`).getUTCDay()] ?? '';
}

/**
 * Формат часов правил объекта (R5). Сервер присылает часы готовыми строками в этом формате; правка вводится с шагом
 * 0,1 ч (проверка сервера, `HOURS_INVALID`), при целых часах — целыми.
 */
export type HoursFormat = { kind: 'decimal'; digits: number } | { kind: 'hmm' };

/**
 * Формат часов по строкам, которые прислал сервер (R5): «9:10» — «чч:мм», «9,3» — десятичные с 1 знаком, «9» — целые.
 * Первая подходящая строка решает. Табель без движка строк не присылает — `null`: часы 001 с шагом 0,1.
 */
export function hoursFormatOf(values: Iterable<string | null | undefined>): HoursFormat | null {
  for (const v of values) {
    if (!v) continue;
    if (/^\d+:\d{2}$/.test(v)) return { kind: 'hmm' };
    const m = /^\d+(?:,(\d+))?$/.exec(v);
    if (m) return { kind: 'decimal', digits: m[1]?.length ?? 0 };
  }
  return null;
}

/** Целые часы: правила объекта показывают часы без дробной части. */
export const wholeHours = (f: HoursFormat | null) => f?.kind === 'decimal' && f.digits === 0;

/** Часы для поля ввода в формате объекта: «8», «8,5», «8:30». */
export function hoursText(h: number, f: HoursFormat | null): string {
  if (f?.kind === 'hmm') {
    const min = Math.round(h * 60);
    return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
  }
  if (wholeHours(f)) return String(Math.round(h));
  return String(Math.round(h * 10) / 10).replace('.', ',');
}
